// src/components/DeepFikaPage.jsx
// Deep Fika — 1-on-1 matched fika experience for ConnectCo members
// Route: /deep-fika

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  UserIcon,
  ClockIcon,
  SparklesIcon,
  ArrowRightIcon,
  HeartIcon,
  ShieldCheckIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  MapPinIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

// ── SEO config (imported statically in App.jsx) ────────────────────────────
export const deepFikaSeoConfig = {
  title: "Deep Fika — One-on-One Matched Fika | Connect & Co Tokyo",
  description: "The most intimate format in the ConnectCo community. One person, matched to you. Sixty minutes at a premium Tokyo café. AI-assisted matching, friendship-first, no agenda. For active members.",
  keywords: "deep fika, one on one tokyo, matched fika, meaningful connection tokyo, connectco fika, 1 on 1 coffee tokyo, intimate connection tokyo",
  url: "https://connectco.app/deep-fika",
  canonical: "https://connectco.app/deep-fika",
  openGraph: {
    title: "Deep Fika — One Conversation. One Person. Everything Intentional.",
    description: "AI-matched 1-on-1 fika for ConnectCo members. Sixty minutes. One person chosen specifically for you. The deepest expression of what fika is for.",
    url: "https://connectco.app/deep-fika",
    image: "https://connectco.app/og-deep-fika.jpg",
    type: "website",
    site_name: "Connect & Co",
    locale: "en_US"
  },
  twitter: {
    card: "summary_large_image",
    title: "Deep Fika — 1-on-1 Matched Fika | Connect & Co",
    description: "Sixty minutes. One person matched specifically for you. The most intimate format in the ConnectCo community.",
    image: "https://connectco.app/og-deep-fika.jpg"
  }
};

// ── Animation helpers ──────────────────────────────────────────────────────
const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
};

const stagger = {
  animate: { transition: { staggerChildren: 0.1 } },
};

// ── Page ──────────────────────────────────────────────────────────────────
const DeepFikaPage = () => {
  const navigate = useNavigate();

  const handleApplyClick = () => {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'deep_fika_apply_click', {
        event_category: 'conversion',
        event_label: 'deep_fika_cta',
        click_location: 'page_cta',
        target_page: '/deep-fika/apply',
      });
    }
    navigate('/deep-fika/apply');
  };

  // ── Data ────────────────────────────────────────────────────────────────

  const howItWorks = [
    {
      step: '01',
      icon: '📝',
      title: 'Apply',
      body: 'Fill out a short application — who you are, what you care about, how you like to talk. Takes about ten minutes.',
    },
    {
      step: '02',
      icon: '🤝',
      title: 'Get matched',
      body: 'We review your application against the pool of active members and find one person whose interests, communication style, and schedule genuinely align with yours. Human review. Not just an algorithm.',
    },
    {
      step: '03',
      icon: '☕',
      title: 'Meet',
      body: 'You each receive an introduction email. From there, you pick a café, pick a time, and show up. Sixty minutes. No agenda. No script.',
    },
  ];

  const philosophy = [
    {
      emoji: '🤫',
      title: 'No safety net',
      body: 'In a group, there is always someone else to fill the silence. In a 1-on-1, you and the other person have to do it together. That pressure — that accountability — is what makes the conversation real.',
    },
    {
      emoji: '🎯',
      title: 'One person, chosen carefully',
      body: "You're not matched to a group dynamic. You're matched to a single person whose interests, pace, and reason for being here actually aligns with yours. The specificity is the point.",
    },
    {
      emoji: '🌿',
      title: 'The fika principle, undiluted',
      body: "Fika has always been about the person across from you, not the coffee in your hand. Deep Fika removes everything that distracts from that — the group, the icebreakers, the performance — and leaves just the conversation.",
    },
  ];

  const rules = [
    {
      allowed: true,
      text: 'Come to make a genuine friend',
    },
    {
      allowed: true,
      text: 'Be honest about who you are and what you\'re looking for',
    },
    {
      allowed: true,
      text: 'Go wherever the conversation leads — personal, philosophical, funny, quiet',
    },
    {
      allowed: true,
      text: 'Tell us if a match didn\'t work. We use that to match better next time.',
    },
    {
      allowed: false,
      text: 'Romantic pursuit — this is not a dating service, not even a little',
    },
    {
      allowed: false,
      text: 'Business pitches, hidden agendas, or LinkedIn follow-ups',
    },
    {
      allowed: false,
      text: 'Treating your match as a professional contact or mentorship opportunity',
    },
    {
      allowed: false,
      text: 'Sharing what your match told you in confidence',
    },
  ];

  const whoFor = [
    "You've come to Monthly Fika or Signature Fika and thought: I want more than this, just with one person",
    "You're introverted and groups are exhausting — a single deep conversation is more your speed",
    "You've just moved to Tokyo and want one real friend, not a network",
    "You have things on your mind you'd say to the right stranger, but haven't found them yet",
    "You believe that one honest hour with a good person is worth more than ten surface-level coffees",
  ];

  const whoNot = [
    "People looking for romance or a date — full stop",
    "Anyone hoping to pitch a business, find clients, or grow their network professionally",
    "Members not yet comfortable being genuinely present with a stranger",
  ];

  const faqs = [
    {
      q: 'Is this included in my membership?',
      a: 'Yes. Deep Fika is included in all ConnectCo memberships — monthly, 6-month, and yearly. No additional cost.',
    },
    {
      q: 'How long does matching take?',
      a: 'Usually 1–2 weeks from application approval. We match manually and only when we\'re confident in the pairing. We\'d rather wait for the right match than rush a bad one.',
    },
    {
      q: 'What if I don\'t click with my match?',
      a: "That's okay. Not every fika becomes a friendship. We ask that you give it the full sixty minutes. Afterward, let us know how it went — your feedback shapes the next match for both of you.",
    },
    {
      q: 'Can I decline a match before we meet?',
      a: "Yes. When you receive your introduction email, you have 48 hours to let us know if something doesn't feel right. We don't share your reason with your match — we just find new pairings for both of you.",
    },
    {
      q: 'Who picks the café and time?',
      a: 'You two decide between yourselves after the introduction. We suggest specialty coffee venues in your preferred neighborhood, but the logistics are up to you.',
    },
    {
      q: 'Is there a limit on how many times I can apply?',
      a: 'No. You can apply for a new Deep Fika after each session. Most members do one every 4–6 weeks.',
    },
  ];

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-stone-50">

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative pt-32 pb-24 bg-gradient-to-br from-stone-900 via-stone-900 to-emerald-950 overflow-hidden min-h-[600px]">
        {/* Background texture */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-20 w-64 h-64 bg-emerald-400 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-amber-400 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-2 bg-emerald-900/60 border border-emerald-700/40 text-emerald-300 text-sm font-medium px-4 py-2 rounded-full mb-8">
              <SparklesIcon className="w-4 h-4" />
              Members Only · Included in all memberships
            </span>
          </motion.div>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight"
          >
            Deep Fika
          </motion.h1>

          <motion.p
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-2xl md:text-3xl text-emerald-300 font-medium mb-6"
          >
            One person. Sixty minutes. No safety net.
          </motion.p>

          <motion.p
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-lg text-stone-300 mb-10 max-w-2xl mx-auto leading-relaxed"
          >
            The most intimate format in the ConnectCo community. You apply, we find
            one person matched specifically to you, and you meet for sixty minutes
            at a premium Tokyo café. That's it. That's the whole thing.
          </motion.p>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <button
              onClick={handleApplyClick}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-lg px-10 py-4 rounded-full hover:from-emerald-400 hover:to-teal-400 hover:scale-105 transition-all duration-300 shadow-lg shadow-emerald-900/40"
            >
              Apply for Deep Fika
              <ArrowRightIcon className="w-5 h-5" />
            </button>
            <span className="text-stone-400 text-sm">Active membership required</span>
          </motion.div>

          {/* Quick stats */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-16 grid grid-cols-3 gap-8 max-w-lg mx-auto"
          >
            {[
              { value: '1', label: 'person, matched to you' },
              { value: '60', label: 'minutes, no agenda' },
              { value: '¥0', label: 'extra cost for members' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-xs text-stone-400">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── PHILOSOPHY ───────────────────────────────────────────────────── */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-emerald-600 text-sm font-semibold uppercase tracking-widest mb-3">Why one-on-one</p>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">
              The group is great. This is different.
            </h2>
            <p className="text-stone-500 text-lg max-w-2xl mx-auto">
              Curated Fika brings 3–4 people together. Deep Fika removes the group
              entirely. Here's why that matters.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {philosophy.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-stone-50 rounded-2xl p-8 border border-stone-100"
              >
                <div className="text-4xl mb-5">{item.emoji}</div>
                <h3 className="text-lg font-bold text-stone-900 mb-3">{item.title}</h3>
                <p className="text-stone-500 leading-relaxed text-sm">{item.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────────────────── */}
      <section className="py-20 px-6 bg-gradient-to-br from-stone-50 to-emerald-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-emerald-600 text-sm font-semibold uppercase tracking-widest mb-3">The process</p>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">How Deep Fika works</h2>
          </motion.div>

          <div className="space-y-6">
            {howItWorks.map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="flex gap-6 bg-white rounded-2xl p-8 border border-stone-100 shadow-sm"
              >
                <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                  <span className="text-2xl">{item.icon}</span>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs font-bold text-emerald-600 tracking-widest">{item.step}</span>
                    <h3 className="text-lg font-bold text-stone-900">{item.title}</h3>
                  </div>
                  <p className="text-stone-500 leading-relaxed">{item.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMMUNITY RULES ──────────────────────────────────────────────── */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <p className="text-emerald-600 text-sm font-semibold uppercase tracking-widest mb-3">Community standards</p>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">The only rules that matter</h2>
            <p className="text-stone-500 text-lg">
              A 1-on-1 is a more intimate setting than any of our other formats.
              That intimacy is only possible when both people are clear on what this is —
              and what it isn't.
            </p>
          </motion.div>

          <div className="bg-stone-50 rounded-2xl border border-stone-200 overflow-hidden">
            {/* Allowed */}
            <div className="p-6 border-b border-stone-200">
              <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-4">This is for</p>
              <div className="space-y-3">
                {rules.filter(r => r.allowed).map((rule) => (
                  <div key={rule.text} className="flex items-start gap-3">
                    <CheckCircleIcon className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <p className="text-stone-700 text-sm">{rule.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Not allowed */}
            <div className="p-6 bg-red-50/40">
              <p className="text-xs font-bold text-red-500 uppercase tracking-widest mb-4">This is not for</p>
              <div className="space-y-3">
                {rules.filter(r => !r.allowed).map((rule) => (
                  <div key={rule.text} className="flex items-start gap-3">
                    <XCircleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-stone-700 text-sm">{rule.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="mt-6 text-center text-stone-400 text-sm">
            These are ConnectCo community standards. Violations result in removal from the program and the community.
            Full guidelines at{' '}
            <a href="/community-guidelines" className="text-emerald-600 underline hover:text-emerald-700 transition-colors">
              connectco.app/community-guidelines
            </a>
          </p>
        </div>
      </section>

      {/* ── WHO IS THIS FOR ───────────────────────────────────────────────── */}
      <section className="py-20 px-6 bg-gradient-to-br from-emerald-50 to-stone-50">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* For */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-emerald-600 text-sm font-semibold uppercase tracking-widest mb-4">Deep Fika might be for you if</p>
              <ul className="space-y-4">
                {whoFor.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="text-emerald-500 font-bold mt-0.5 flex-shrink-0">—</span>
                    <p className="text-stone-700">{item}</p>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Not for */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <p className="text-stone-400 text-sm font-semibold uppercase tracking-widest mb-4">Deep Fika is probably not right if</p>
              <ul className="space-y-4 mb-8">
                {whoNot.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="text-stone-300 font-bold mt-0.5 flex-shrink-0">—</span>
                    <p className="text-stone-500">{item}</p>
                  </li>
                ))}
              </ul>
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-5">
                <p className="text-amber-800 text-sm font-medium mb-1">Not a member yet?</p>
                <p className="text-amber-700 text-sm mb-3">
                  Deep Fika is included in all ConnectCo memberships. Join first, then apply.
                </p>
                <button
                  onClick={() => navigate('/subscription')}
                  className="text-sm font-semibold text-amber-700 underline underline-offset-2 hover:text-amber-800 transition-colors"
                >
                  View membership options →
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────────── */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-stone-900">Common questions</h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <motion.div
                key={faq.q}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.07 }}
                className="bg-stone-50 rounded-xl border border-stone-100 px-6 py-5"
              >
                <p className="font-semibold text-stone-900 mb-2">{faq.q}</p>
                <p className="text-stone-500 text-sm leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMPARE FORMATS ─────────────────────────────────────────────── */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <p className="text-emerald-600 text-sm font-semibold uppercase tracking-widest mb-3">Find your format</p>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">
              Choose your fika experience
            </h2>
            <p className="text-stone-500 text-lg max-w-2xl mx-auto">
              From community gatherings to the most intimate conversation you'll have this month.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {/* Monthly Fika */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-stone-50 rounded-2xl p-6 border border-stone-200"
            >
              <div className="text-center mb-5">
                <span className="text-3xl block mb-2">📅</span>
                <h3 className="text-lg font-bold text-stone-900">Monthly Fika</h3>
                <p className="text-stone-400 text-xs mt-1">Community gateway</p>
              </div>
              <ul className="space-y-2.5 mb-6 text-sm text-stone-600">
                <li className="flex items-start gap-2"><CheckCircleIcon className="w-4 h-4 text-stone-400 flex-shrink-0 mt-0.5" /><span>25–40 people</span></li>
                <li className="flex items-start gap-2"><CheckCircleIcon className="w-4 h-4 text-stone-400 flex-shrink-0 mt-0.5" /><span>Twice monthly (Sat + Sun)</span></li>
                <li className="flex items-start gap-2"><CheckCircleIcon className="w-4 h-4 text-stone-400 flex-shrink-0 mt-0.5" /><span>All subscribers</span></li>
              </ul>
              <button
                onClick={() => navigate('/monthly-fika')}
                className="w-full border border-stone-300 text-stone-600 py-2.5 rounded-xl text-sm font-semibold hover:border-stone-400 hover:bg-stone-100 transition-all"
              >
                Learn more
              </button>
            </motion.div>

            {/* Signature Fika */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-stone-50 rounded-2xl p-6 border border-stone-200"
            >
              <div className="text-center mb-5">
                <span className="text-3xl block mb-2">⭐</span>
                <h3 className="text-lg font-bold text-stone-900">Signature Fika</h3>
                <p className="text-stone-400 text-xs mt-1">Weekly ritual</p>
              </div>
              <ul className="space-y-2.5 mb-6 text-sm text-stone-600">
                <li className="flex items-start gap-2"><CheckCircleIcon className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" /><span>5–10 people</span></li>
                <li className="flex items-start gap-2"><CheckCircleIcon className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" /><span>Every Wednesday</span></li>
                <li className="flex items-start gap-2"><CheckCircleIcon className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" /><span>Premium venues</span></li>
              </ul>
              <button
                onClick={() => navigate('/signature-fika')}
                className="w-full border border-amber-400 text-amber-700 py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-50 transition-all"
              >
                Learn more
              </button>
            </motion.div>

            {/* Curated Fika */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-stone-50 rounded-2xl p-6 border border-stone-200"
            >
              <div className="text-center mb-5">
                <span className="text-3xl block mb-2">✨</span>
                <h3 className="text-lg font-bold text-stone-900">Curated Fika</h3>
                <p className="text-stone-400 text-xs mt-1">Micro-group</p>
              </div>
              <ul className="space-y-2.5 mb-6 text-sm text-stone-600">
                <li className="flex items-start gap-2"><CheckCircleIcon className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" /><span>3–4 people, matched</span></li>
                <li className="flex items-start gap-2"><CheckCircleIcon className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" /><span>AI-assisted curation</span></li>
                <li className="flex items-start gap-2"><CheckCircleIcon className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" /><span>Application required</span></li>
              </ul>
              <button
                onClick={() => navigate('/curated-fika')}
                className="w-full border border-purple-300 text-purple-700 py-2.5 rounded-xl text-sm font-semibold hover:bg-purple-50 transition-all"
              >
                Learn more
              </button>
            </motion.div>

            {/* Deep Fika — highlighted */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border-2 border-emerald-400 relative"
            >
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-4 py-1 rounded-full text-xs font-semibold">
                The Deepest Format
              </div>
              <div className="text-center mb-5">
                <span className="text-3xl block mb-2">🫂</span>
                <h3 className="text-lg font-bold text-stone-900">Deep Fika</h3>
                <p className="text-emerald-600 text-xs mt-1 font-medium">1-on-1 matched</p>
              </div>
              <ul className="space-y-2.5 mb-6 text-sm text-stone-700">
                <li className="flex items-start gap-2"><CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /><span>1 person, matched to you</span></li>
                <li className="flex items-start gap-2"><CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /><span>60 min, no agenda</span></li>
                <li className="flex items-start gap-2"><CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /><span>Mutual acceptance</span></li>
                <li className="flex items-start gap-2"><CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /><span>Friendship only</span></li>
              </ul>
              <div className="text-center text-sm text-emerald-700 bg-emerald-100 py-2 px-4 rounded-lg font-medium">
                You are here
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────────────── */}
      <section className="py-24 px-6 bg-gradient-to-br from-stone-900 to-emerald-950">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-emerald-400 text-sm font-semibold uppercase tracking-widest mb-4">Ready?</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-5">
              Apply for Deep Fika
            </h2>
            <p className="text-stone-300 text-lg mb-8 leading-relaxed">
              The application takes about ten minutes. Tell us who you are,
              what you care about, and when you're free. We'll find your person.
            </p>
            <button
              onClick={handleApplyClick}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-lg px-10 py-4 rounded-full hover:from-emerald-400 hover:to-teal-400 hover:scale-105 transition-all duration-300 shadow-xl shadow-emerald-900/40"
            >
              Apply Now
              <ArrowRightIcon className="w-5 h-5" />
            </button>
            <p className="mt-4 text-stone-500 text-sm">
              Questions?{' '}
              <a href="mailto:hello@connectco.app" className="text-stone-400 underline hover:text-stone-200 transition-colors">
                hello@connectco.app
              </a>
            </p>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default DeepFikaPage;
